// src/App.jsx - Main Application Component
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetails from './pages/ClientDetails';
import Assessments from './pages/Assessments';
import CreateAssessment from './pages/CreateAssessment';
import AssessmentDetails from './pages/AssessmentDetails';
import AssessmentTake from './pages/AssessmentTake';
import AssessmentResults from './pages/AssessmentResults';
import TrendingReport from './pages/TrendingReport';
import AdminPanel from './pages/AdminPanel';
import VulnerabilityScans from './pages/VulnerabilityScans';
import VulnerabilityScanDetails from './pages/VulnerabilityScanDetails';
import VulnerabilityComparison from './pages/VulnerabilityComparison';
import PenetrationTests from './pages/PenetrationTests';
import PenetrationTestDetails from './pages/PenetrationTestDetails';
import PenetrationTestComparison from './pages/PenetrationTestComparison';
import ActiveDirectoryAudits from './pages/ActiveDirectoryAudits';
import ActiveDirectoryAuditDetails from './pages/ActiveDirectoryAuditDetails';
import ActiveDirectoryComparison from './pages/ActiveDirectoryComparison';
import AuthCallback from './pages/AuthCallback';
import NotFound from './pages/NotFound';
import VorAssessments from './pages/VorAssessments';
import VorNewAssessment from './pages/VorNewAssessment';
import VorAssessmentDetail from './pages/VorAssessmentDetail';
import VorAssessmentWorkflow from './pages/VorAssessmentWorkflow';
import VorAdmin from './pages/VorAdmin';
import NarAssessments from './pages/NarAssessments';
import NarConduct from './pages/NarConduct';
import NarReport from './pages/NarReport';
import NarAdmin from './pages/NarAdmin';
import DarkWeb from './pages/DarkWeb';
import AiGovernance from './pages/AiGovernance';
import AiGovernanceProgram from './pages/AiGovernanceProgram';
import AiGovernanceAdmin from './pages/AiGovernanceAdmin';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Protected routes */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/" element={<Dashboard />} />
              
              {/* Clients */}
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/:clientId" element={<ClientDetails />} />
              
              {/* Assessments */}
              <Route element={<ProtectedRoute requireModule="assessments"><Outlet /></ProtectedRoute>}>
                <Route path="/assessments" element={<Assessments />} />
                <Route path="/assessments/new" element={<CreateAssessment />} />
                <Route path="/assessments/:assessmentId" element={<AssessmentDetails />} />
                <Route path="/assessments/:assessmentId/take" element={<AssessmentTake />} />
                <Route path="/assessments/:assessmentId/results" element={<AssessmentResults />} />
                <Route path="/trending-report" element={<TrendingReport />} />
              </Route>

              {/* Vulnerability Scans */}
              <Route element={<ProtectedRoute requireModule="vulnerability-scans"><Outlet /></ProtectedRoute>}>
                <Route path="/vulnerability-scans" element={<VulnerabilityScans />} />
                <Route path="/vulnerability-scans/:scanId" element={<VulnerabilityScanDetails />} />
                <Route path="/vulnerability-scans/:scanId/compare" element={<VulnerabilityComparison />} />
              </Route>

              {/* Penetration Tests */}
              <Route element={<ProtectedRoute requireModule="penetration-tests"><Outlet /></ProtectedRoute>}>
                <Route path="/penetration-tests" element={<PenetrationTests />} />
                <Route path="/penetration-tests/:pentestId" element={<PenetrationTestDetails />} />
                <Route path="/penetration-tests/:pentestId/compare" element={<PenetrationTestComparison />} />
              </Route>

              {/* Active Directory Audits */}
              <Route element={<ProtectedRoute requireModule="ad-audits"><Outlet /></ProtectedRoute>}>
                <Route path="/active-directory-audits" element={<ActiveDirectoryAudits />} />
                <Route path="/active-directory-audits/:auditId" element={<ActiveDirectoryAuditDetails />} />
                <Route path="/active-directory-audits/:auditId/compare" element={<ActiveDirectoryComparison />} />
              </Route>

              {/* Value of Risk (VoR) */}
              <Route element={<ProtectedRoute requireModule="value-of-risk"><Outlet /></ProtectedRoute>}>
                <Route path="/vor/assessments" element={<VorAssessments />} />
                <Route path="/vor/assessments/new" element={<VorNewAssessment />} />
                <Route path="/vor/assessments/:assessmentId" element={<VorAssessmentDetail />} />
                <Route path="/vor/assessments/:assessmentId/workflow" element={<VorAssessmentWorkflow />} />
                <Route path="/vor/admin" element={<ProtectedRoute requireAdmin><VorAdmin /></ProtectedRoute>} />
              </Route>

              {/* Network Admin Review (NAR) */}
              <Route element={<ProtectedRoute requireModule="nar"><Outlet /></ProtectedRoute>}>
                <Route path="/nar/assessments" element={<NarAssessments />} />
                <Route path="/nar/assessments/:narId/conduct" element={<NarConduct />} />
                <Route path="/nar/assessments/:narId/report" element={<NarReport />} />
                <Route path="/nar/admin" element={<ProtectedRoute requireAdmin><NarAdmin /></ProtectedRoute>} />
              </Route>

              {/* Dark Web */}
              <Route path="/dark-web" element={<DarkWeb />} />

              {/* AI Governance */}
              <Route element={<ProtectedRoute requireModule="ai-governance"><Outlet /></ProtectedRoute>}>
                <Route path="/ai-gov" element={<AiGovernance />} />
                <Route path="/ai-gov/programs/:programId" element={<AiGovernanceProgram />} />
                <Route path="/ai-gov/admin" element={<ProtectedRoute requireAdmin><AiGovernanceAdmin /></ProtectedRoute>} />
              </Route>

              {/* Admin */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminPanel />
                  </ProtectedRoute>
                }
              />
            </Route>
            
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
        <ToastContainer position="top-right" autoClose={3000} />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
